import { AuthService } from 'services/AuthService'
import { webLogger } from 'utils/logger'

const HTTP_UNAUTHORIZED = 401
const SSE_DATA_PREFIX = 'data: '

type QueryValue = string | number | boolean | undefined

type RequestOptions = {
  auth?: boolean | 'optional'
  headers?: HeadersInit
  query?: Record<string, QueryValue>
  signal?: AbortSignal
}

export type EventStreamItem<T> = {
  id: string
  data: T
}

export class BackendHttpError extends Error {
  readonly status: number

  constructor(status: number, message: string) {
    super(message)
    this.name = 'BackendHttpError'
    this.status = status
  }
}

export class EventStreamProtocolError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'EventStreamProtocolError'
  }
}

export const resolveApiBaseUrl = (environment: NodeJS.ProcessEnv = process.env): string => {
  return environment.REACT_APP_API_URL?.trim() ?? ''
}

const API_BASE_URL = resolveApiBaseUrl()

const buildPath = (path: string): string => {
  const trimmedBase = API_BASE_URL.replace(/\/$/, '')
  if (path.startsWith('/')) {
    return `${trimmedBase}${path}`
  }

  return `${trimmedBase}/api/${path}`
}

const buildQuery = (query?: Record<string, QueryValue>): string => {
  if (!query) {
    return ''
  }
  const parameters = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue
    }
    parameters.set(key, String(value))
  }
  const result = parameters.toString()
  return result ? `?${result}` : ''
}

const getAuthToken = async (forceRefresh = false): Promise<string | undefined> => {
  return AuthService.getAccessToken(forceRefresh)
}

const withAuthHeaders = async (options?: RequestOptions, forceRefresh = false): Promise<HeadersInit> => {
  const headers = new Headers(options?.headers)

  if (options?.auth === false) {
    return headers
  }

  const token = await getAuthToken(forceRefresh)
  if (!token) {
    if (options?.auth === 'optional') {
      return headers
    }
    throw new Error('Authentication required. Please sign in.')
  }

  headers.set('Authorization', `Bearer ${token}`)
  return headers
}

const getErrorMessage = (payload: unknown, fallback: string): string => {
  if (typeof payload === 'object' && payload !== null && 'message' in payload) {
    const message = (payload as { message?: unknown }).message
    if (typeof message === 'string' && message.trim().length > 0) {
      return message
    }
  }
  return fallback
}

const parseJsonResponse = async <T>(response: Response): Promise<T> => {
  if (response.ok) {
    const data: unknown = await response.json()
    return data as T
  }

  const errorPayload: unknown = await response.json().catch(() => ({ message: 'Request failed' }))
  const message = getErrorMessage(errorPayload, `Request failed: ${response.status}`)
  throw new BackendHttpError(response.status, message)
}

const normalizeNewlines = (value: string): string => value.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

const parseEventStreamFrame = <T>(frame: string): EventStreamItem<T> | undefined => {
  let id: string | undefined
  const dataLines: string[] = []

  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) {
      continue
    }
    const separatorIndex = line.indexOf(':')
    const field = separatorIndex === -1 ? line : line.slice(0, separatorIndex)
    let value = separatorIndex === -1 ? '' : line.slice(separatorIndex + 1)
    if (value.startsWith(' ')) {
      value = value.slice(1)
    }

    if (field === 'id') {
      if (value.includes('\0')) {
        throw new EventStreamProtocolError('Event stream ID contains a null character')
      }
      id = value
    } else if (field === 'data') {
      dataLines.push(value)
    }
  }

  if (dataLines.length === 0) {
    return undefined
  }
  if (id === undefined || !/^\d+$/.test(id)) {
    throw new EventStreamProtocolError('Event stream event is missing an integer ID')
  }

  const rawData = dataLines.join('\n')
  try {
    return { id, data: JSON.parse(rawData) as T }
  } catch (error) {
    webLogger.error('Invalid event stream JSON', { error })
    throw new EventStreamProtocolError('Event stream event contains invalid JSON')
  }
}

const fetchEventStreamResponse = async (
  path: string,
  options: RequestOptions | undefined,
  forceRefresh: boolean
): Promise<Response> => {
  const headers = new Headers(await withAuthHeaders(options, forceRefresh))
  headers.set('Accept', 'text/event-stream')
  const url = `${buildPath(path)}${buildQuery(options?.query)}`
  return fetch(url, { method: 'GET', headers, signal: options?.signal })
}

export const BackendService = {
  async GET<T>(path: string, options?: RequestOptions): Promise<T> {
    const headers = await withAuthHeaders(options)
    const url = `${buildPath(path)}${buildQuery(options?.query)}`
    const response = await fetch(url, {
      method: 'GET',
      headers,
      signal: options?.signal,
    })

    return parseJsonResponse<T>(response)
  },

  async POST<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
    const headers = new Headers(await withAuthHeaders(options))
    if (!isFormData) {
      headers.set('Content-Type', 'application/json')
    }
    const url = `${buildPath(path)}${buildQuery(options?.query)}`
    let requestBody: BodyInit | null | undefined
    if (body instanceof FormData) {
      requestBody = body
    } else if (body === undefined) {
      requestBody = undefined
    } else {
      requestBody = JSON.stringify(body)
    }
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: requestBody,
      signal: options?.signal,
    })

    return parseJsonResponse<T>(response)
  },

  async PATCH<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
    const headers = new Headers(await withAuthHeaders(options))
    headers.set('Content-Type', 'application/json')
    const url = `${buildPath(path)}${buildQuery(options?.query)}`
    const payload = body === undefined ? undefined : JSON.stringify(body)
    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: payload,
      signal: options?.signal,
    })

    return parseJsonResponse<T>(response)
  },

  async *STREAM<T>(path: string, body?: unknown, options?: RequestOptions): AsyncGenerator<T, void> {
    const headers = new Headers(await withAuthHeaders(options))
    headers.set('Content-Type', 'application/json')
    const url = `${buildPath(path)}${buildQuery(options?.query)}`
    const payload = body === undefined ? undefined : JSON.stringify(body)
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: payload,
      signal: options?.signal,
    })

    if (!response.ok) {
      const errorPayload: unknown = await response.json().catch(() => ({ message: 'Stream request failed' }))
      const message = getErrorMessage(errorPayload, `Stream request failed: ${response.status}`)
      throw new Error(message)
    }

    if (!response.body) {
      throw new Error('No response body')
    }
    const reader = response.body.getReader()

    const decoder = new TextDecoder()
    let buffer = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) {
        break
      }

      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        if (!line.startsWith('data: ')) {
          continue
        }
        const data = line.slice(SSE_DATA_PREFIX.length)
        try {
          const parsed = JSON.parse(data) as T
          yield parsed
        } catch (error) {
          webLogger.warn('Failed to parse SSE data from backend stream', { data, error })
        }
      }
    }
  },

  // eslint-disable-next-line complexity -- SSE framing handles transport, auth refresh, and cleanup in one generator.
  async *EVENT_STREAM<T>(path: string, options?: RequestOptions): AsyncGenerator<EventStreamItem<T>, void> {
    let response = await fetchEventStreamResponse(path, options, false)
    if (response.status === HTTP_UNAUTHORIZED) {
      response = await fetchEventStreamResponse(path, options, true)
    }
    if (!response.ok) {
      const errorPayload: unknown = await response.json().catch(() => ({ message: 'Stream request failed' }))
      throw new BackendHttpError(
        response.status,
        getErrorMessage(errorPayload, `Stream request failed: ${response.status}`)
      )
    }
    if (!response.body) {
      throw new EventStreamProtocolError('Event stream response has no body')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    try {
      for (;;) {
        const { done, value } = await reader.read()
        buffer += decoder.decode(value, { stream: !done })
        const hasSplitCarriageReturn = !done && buffer.endsWith('\r')
        const parsableBuffer = normalizeNewlines(hasSplitCarriageReturn ? buffer.slice(0, -1) : buffer)
        const frames = parsableBuffer.split('\n\n')
        buffer = `${frames.pop() ?? ''}${hasSplitCarriageReturn ? '\r' : ''}`
        for (const frame of frames) {
          const event = parseEventStreamFrame<T>(frame)
          if (event) {
            yield event
          }
        }
        if (done) {
          break
        }
      }

      if (buffer.trim().length > 0) {
        try {
          const event = parseEventStreamFrame<T>(buffer)
          if (event) {
            yield event
          }
        } catch (error) {
          if (!(error instanceof EventStreamProtocolError) || !error.message.includes('invalid JSON')) {
            throw error
          }
        }
      }
    } finally {
      try {
        await reader.cancel()
      } catch {
        // The reader may already be closed or aborted.
      }
      reader.releaseLock()
    }
  },
}
